// Peer recognition — talks to performance-service via the gateway
// (/api/v1/performance/recognition/*). gatewayApi attaches the token + refresh.
import { gatewayApi } from '@/lib/hrApi';

const BASE = '/api/v1/performance/recognition';
const data = <T,>(p: Promise<{ data: T }>) => p.then((r) => r.data);

export interface RecognitionCategory {
  key: string;
  label: string;
}

export interface Recognition {
  id: string;
  recognizer_user_id: string;
  recipient_user_id: string;
  category_key: string;
  title: string;
  message: string;
  relationship_type: string;
  source_domain?: string;
  source_entity_id?: string;
  ai_suggested: boolean;
  source: string;
  created_at: string;
}

/** A name-resolved user reference (recipients + tags on the feed). */
export interface RecognitionPerson {
  user_id: string;
  name: string;
}

export interface RecognitionInput {
  recipient_user_id: string;
  recipient_employee_id?: string;
  /** Additional people credited on the SAME post (multi-recipient). */
  additional_recipient_user_ids?: string[];
  /** Colleagues tagged on the post (notified + it shows in their feed). */
  tagged_user_ids?: string[];
  category_key: string;
  title: string;
  message?: string;
  relationship_type?: string;
  source_domain?: string;
  source_entity_type?: string;
  source_entity_id?: string;
  source_comment_id?: string;
  /** Set when accepting a Dana suggestion, so the candidate is marked completed. */
  candidate_id?: string;
  ai_suggested?: boolean;
  media_key?: string;  // from uploadRecognitionMedia
  media_type?: string;
}

/** A Dana contextual suggestion (OFFERED candidate). Dana suggests; the human
 *  confirms — accepting creates a real recognition. Never auto-created. */
export interface RecognitionCandidate {
  id: string;
  subject_user_id?: string;
  subject_employee_id?: string;
  source_entity_id?: string;
  confidence: number;
  reason_summary: string;
  suggested_title: string;
  suggested_message: string;
  suggested_category: string;
  suggested_tags: string[];
  created_at: string;
}

export const getRecognitionCategories = () =>
  data<RecognitionCategory[]>(gatewayApi.get(`${BASE}/categories`));

export const getRecognitionReceived = () => data<Recognition[]>(gatewayApi.get(`${BASE}`));

export const getRecognitionGiven = () => data<Recognition[]>(gatewayApi.get(`${BASE}/given`));

// ── Recognition from a conversation ──────────────────────────────────────────
// performance-service owns the interpretation; conversations supply context and
// presentation. This SUGGESTS only — creation stays createRecognition below,
// where every authorisation rule already lives.

export interface RecognitionContextMessage {
  message_id: string;
  author_user_id: string;
  text: string;
  created_at: string;
}

export interface RecognitionSuggestInput {
  draft_text: string;
  conversation_id: string;
  reply_to_message_id?: string;
  /** People explicitly @mentioned in the draft. Explicit beats inferred. */
  explicit_recipient_user_ids?: string[];
  context_messages?: RecognitionContextMessage[];
}

export interface RecognitionRecipientCandidate {
  user_id: string;
  reason: 'explicit_mention' | 'reply_author' | 'context_author';
  score: number;
}

export interface RecognitionSuggestion {
  recipient?: RecognitionRecipientCandidate;
  candidates: RecognitionRecipientCandidate[];
  /** Nobody is clearly indicated — ask, never guess. */
  needs_recipient: boolean;
  contribution?: string;
  suggested_category_key?: string;
  suggested_title?: string;
  suggested_message?: string;
  /** Neither draft nor context says what the person actually did. */
  needs_detail: boolean;
  /** The classifier was unreachable; this is the deterministic fallback. */
  degraded: boolean;
  /** This workspace cannot create a conversation-sourced recognition at all. */
  blocked: boolean;
  blocked_reason?: string;
}

export const suggestRecognition = (input: RecognitionSuggestInput, signal?: AbortSignal) =>
  data<RecognitionSuggestion>(gatewayApi.post(`${BASE}/suggest`, input, { signal }));

export const createRecognition = (input: RecognitionInput) =>
  data<Recognition>(gatewayApi.post(`${BASE}`, input));

/** Dana's offered recognition suggestions for a work item — scoped to the caller
 *  (only suggestions the caller may act on are returned). */
export const getRecognitionCandidates = (workItemId: string) =>
  data<RecognitionCandidate[]>(
    gatewayApi.get(`${BASE}/candidates`, { params: { work_item_id: workItemId } }),
  );

// ---- Recognition Community social layer (Phase 1) ---------------------------

export interface RecognitionComment {
  id: string;
  recognition_id: string;
  user_id: string;
  author_name: string;
  body: string;
  created_at: string;
}

/** A recognition enriched for the tenant feed. */
export interface FeedItem extends Recognition {
  recognizer_name: string;
  recipient_name: string;
  recipients?: RecognitionPerson[]; // ALL recipients (primary + additional)
  tags?: RecognitionPerson[];       // tagged colleagues
  category_label: string;
  reactions: Record<string, number>; // kind -> count
  my_reactions: string[];
  comment_count: number;
  recent_comments: RecognitionComment[];
  media_url?: string;  // short-lived presigned image URL
  media_type?: string;
}

export interface RecognitionFeedPage {
  items: FeedItem[];
  next_cursor?: string;
}

export interface ReactResult {
  on: boolean;
  kind: string;
  reactions: Record<string, number>;
}

/** Recognition feed (newest first). scope filters to the caller's team or the
 *  people they follow. */
export const getRecognitionFeed = (cursor?: string, limit = 20, scope?: 'team' | 'following') =>
  data<RecognitionFeedPage>(
    gatewayApi.get(`${BASE}/feed`, { params: { ...(cursor ? { cursor } : {}), limit, ...(scope ? { scope } : {}) } }),
  );

// ---- Follow / subscribe -----------------------------------------------------

export interface FollowPerson {
  user_id: string;
  name: string;
  sublabel?: string;
  following: boolean;
}

/** People the caller follows. */
export const getRecognitionFollows = () => data<FollowPerson[]>(gatewayApi.get(`${BASE}/follows`));

/** Colleagues to recognize/follow (roster minus self + already-followed). */
export const getPeopleSuggestions = () => data<FollowPerson[]>(gatewayApi.get(`${BASE}/people/suggestions`));

export const followPerson = (followee_user_id: string) =>
  gatewayApi.post(`${BASE}/follows`, { followee_user_id }).then((r) => r.data);

export const unfollowPerson = (userId: string) =>
  gatewayApi.delete(`${BASE}/follows/${userId}`).then((r) => r.data);

/** Toggle a reaction (applaud | celebrate | support | love) on a recognition. */
export const reactToRecognition = (id: string, kind = 'applaud') =>
  data<ReactResult>(gatewayApi.post(`${BASE}/${id}/reactions`, { kind }));

export const listRecognitionComments = (id: string) =>
  data<RecognitionComment[]>(gatewayApi.get(`${BASE}/${id}/comments`));

export const addRecognitionComment = (id: string, body: string) =>
  data<RecognitionComment>(gatewayApi.post(`${BASE}/${id}/comments`, { body }));

export const deleteRecognitionComment = (commentId: string) =>
  gatewayApi.delete(`${BASE}/comments/${commentId}`).then((r) => r.data);

/** Ask Dana to draft a recognition message (on-demand). Tenant-scoped AI; returns
 *  '' on failure so the composer can fall back to a local template. */
export const draftRecognition = (recipient_name: string, category: string, note?: string) =>
  gatewayApi
    .post<{ message: string }>('/api/v1/ai/recognition/draft', { recipient_name, category, note: note || '' })
    .then((r) => r.data?.message || '')
    .catch(() => '');

// ---- Celebrations (Phase 2a: anniversaries) ---------------------------------

export interface Celebration {
  employee_id: string;
  user_id?: string | null;
  first_name: string;
  last_name: string;
  kind: string; // anniversary | birthday
  years?: number;
  date: string; // YYYY-MM-DD upcoming occurrence
}

/** Upcoming tenant celebrations (anniversaries) for the feed rail. */
export const getCelebrations = () => data<Celebration[]>(gatewayApi.get(`${BASE}/celebrations`));

// ---- Media uploads (Phase 2b) -----------------------------------------------

export interface UploadedMedia { media_key: string; media_type: string; media_url: string }

/** Upload an image/GIF for a recognition; returns the key to include in
 *  createRecognition (media_key) plus a preview URL. 501 if not configured. */
export const uploadRecognitionMedia = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return gatewayApi.post<UploadedMedia>(`${BASE}/media`, fd).then((r) => r.data);
};

// ---- Analytics (recognition:analytics) --------------------------------------

export interface RecognitionPersonCount {
  user_id: string;
  name: string;
  count: number;
}

export interface RecognitionCategoryCount {
  key: string;
  label: string;
  count: number;
}

export interface RecognitionWeekBucket {
  week_start: string; // ISO date (Monday, UTC)
  count: number;
}

/** One provenance bucket: where a recognition was given from. */
export interface RecognitionSourceCount {
  key: string;
  label: string;
  count: number;
  /** Given AT the work (a conversation, a work item, a goal…) rather than
   *  composed from scratch afterwards. */
  in_flow: boolean;
}

export interface RecognitionAnalytics {
  total: number;
  distinct_recipients: number;
  distinct_recognizers: number;
  from_work: number;
  from_other: number;
  /** ⚠️ Work-sourced ONLY — it shipped with that meaning and kept it. For the
   *  real in-flow share use in_flow_recognition_rate, which counts every
   *  surface including conversations. */
  contextual_recognition_rate: number; // 0..1
  /** Added later: absent when the backend is older than the frontend, which
   *  prod has been more than once. Always read these with a fallback. */
  in_flow?: number;
  in_flow_recognition_rate?: number;
  from_conversation?: number;
  by_source?: RecognitionSourceCount[];
  ai_suggested?: number;
  ai_suggested_rate?: number;
  by_category: Record<string, number>;
  by_category_labeled: RecognitionCategoryCount[];
  by_relationship: Record<string, number>;
  top_recipients: RecognitionPersonCount[];
  top_recognizers: RecognitionPersonCount[];
  weekly_trend: RecognitionWeekBucket[];
}

/** Tenant-wide recognition analytics (gated recognition:analytics). */
export const getRecognitionAnalytics = () =>
  data<RecognitionAnalytics>(gatewayApi.get(`${BASE}/analytics`));

// ---- GIF picker (Giphy proxy) -----------------------------------------------

export interface Gif { id: string; url: string; preview: string; title?: string }

/** Search Giphy via the server-side proxy (empty q = trending). */
export const searchGiphy = (q: string, limit = 24) =>
  data<Gif[]>(gatewayApi.get(`${BASE}/giphy/search`, { params: { q, limit } }));
